/**
 * AYU Programme — 8-Wochen-Kurse
 * Kursübersicht + Modul-Detailansicht
 * Design: Ayurveda Garden — warm, natürlich, motivierend
 */
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useRoute, Link, useLocation } from "wouter";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Circle,
  Clock,
  GraduationCap,
  Lock,
  Play,
  Sparkles,
  Star,
  Trophy,
  Loader2,
  ChevronRight,
  Pause,
  RotateCcw,
  XCircle,
  Headphones,
  Utensils,
} from "lucide-react";
import { useState } from "react";
import { useI18n } from "@/contexts/I18nContext";

// ═══════════════════════════════════════════════════════════
//  Programm-Übersicht (Liste aller Programme)
// ═══════════════════════════════════════════════════════════
function ProgramList({ clientId, isPremium }: { clientId: string; isPremium: boolean }) {
  const { t } = useI18n();
  const { data: programs, isLoading } = trpc.programs.list.useQuery({ clientId });
  const enrollMutation = trpc.programs.enroll.useMutation();
  const kursCheckoutMutation = trpc.stripe.createKursCheckout.useMutation();
  const utils = trpc.useUtils();

  const handleEnroll = async (programId: string) => {
    try {
      await enrollMutation.mutateAsync({ clientId, programId });
      utils.programs.list.invalidate();
      toast.success(t("programme.eingeschrieben"), {
        description: t("programme.eingeschriebenDesc"),
      });
    } catch (err: any) {
      toast.error(err.message || t("programme.einschreibungFehler"));
    }
  };

  const PROGRAM_TO_KURS: Record<string, "kurs_stress_resilienz" | "kurs_darm_gesundheit" | "kurs_saisonaler_detox"> = {
    stress_resilienz: "kurs_stress_resilienz",
    darm_gesundheit: "kurs_darm_gesundheit",
    saisonaler_detox: "kurs_saisonaler_detox",
  };

  const handleBuyKurs = async (programId: string) => {
    const kursId = PROGRAM_TO_KURS[programId];
    if (!kursId) return;
    try {
      const result = await kursCheckoutMutation.mutateAsync({
        kursId,
        origin: window.location.origin,
      });
      if (result.url) {
        toast.info(t("programme.checkoutRedirecting"));
        window.open(result.url, "_blank");
      }
    } catch (err: any) {
      toast.error(err.message || t("programme.checkoutFehler"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#6b7c45]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <BookOpen className="w-6 h-6 text-[#6b7c45]" />
          <h1 className="text-2xl font-bold text-[#2c3e1f]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("programme.katalog")}
          </h1>
        </div>
        <p className="text-sm text-[#6b7c45] ml-9">{t("programme.katalogSubtitle")}</p>
      </div>

      {/* 2x2 Grid Programm-Karten (MENTO-Stil) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {programs?.map((program) => {
          const isEnrolled = !!program.enrollment;
          const isCompleted = program.enrollment?.status === "completed";
          const isLocked = program.accessLevel === "premium" && !isPremium;
          const isPurchasable = program.accessLevel === "purchasable" && !isPremium && !program.isPurchased;
          const hasPurchased = program.isPurchased;

          return (
            <Card
              key={program.id}
              className={`relative overflow-hidden border-[#d4c9a8]/40 hover:shadow-lg transition-all duration-300 ${
                isLocked ? "opacity-75" : ""
              }`}
              style={{ background: "linear-gradient(145deg, #ffffff 0%, #f8f5ef 100%)" }}
            >
              <CardContent className="p-6">
                {/* Icon + Status Badge */}
                <div className="flex items-start justify-between mb-4">
                  <span className="text-4xl">{program.icon}</span>
                  <div className="flex items-center gap-2">
                    {isCompleted && (
                      <Badge className="bg-[#6b7c45] text-white text-xs">
                        <Trophy className="w-3 h-3 mr-1" /> {t("programme.abgeschlossenBadge")}
                      </Badge>
                    )}
                    {isEnrolled && !isCompleted && (
                      <Badge className="bg-[#6b7c45]/20 text-[#6b7c45] text-xs border border-[#6b7c45]/30">
                        <Play className="w-3 h-3 mr-1" /> {t("programme.aktiv")}
                      </Badge>
                    )}
                    {isLocked && (
                      <Badge className="bg-[#c9a84c]/15 text-[#8b6914] text-xs border border-[#c9a84c]/30">
                        <Lock className="w-3 h-3 mr-1" /> {t("programme.dna")}
                      </Badge>
                    )}
                    {program.price && (
                      <Badge className="bg-[#6b7c45]/15 text-[#6b7c45] text-xs font-bold border border-[#6b7c45]/30">
                        {program.price} €
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Titel + Subtitle */}
                <h2
                  className="text-lg font-bold text-[#2c3e1f] mb-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {program.title}
                </h2>
                <p className="text-sm text-[#6b7c45] italic mb-3">{program.subtitle}</p>

                {/* Meta-Info */}
                <div className="flex items-center gap-4 mb-3 text-xs text-[#8a7d65]">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {t("programme.wochen", { n: String(program.durationWeeks) })}
                  </span>
                  <span className="flex items-center gap-1">
                    <GraduationCap className="w-3.5 h-3.5" />
                    {t("programme.aufgaben", { n: String(program.totalTasks) })}
                  </span>
                </div>

                {/* Beschreibung (gekürzt) */}
                <p className="text-sm text-[#5a5a4a] leading-relaxed line-clamp-2 mb-4">
                  {program.description}
                </p>

                {/* Fortschrittsbalken für eingeschriebene Programme */}
                {isEnrolled && !isCompleted && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-[#8a7d65] mb-1">
                      <span>{t("programme.wocheVon", { current: String(program.enrollment!.currentWeek), total: String(program.durationWeeks) })}</span>
                      <span>{Math.round((program.enrollment!.currentWeek / program.durationWeeks) * 100)}%</span>
                    </div>
                    <Progress
                      value={(program.enrollment!.currentWeek / program.durationWeeks) * 100}
                      className="h-1.5 bg-[#e8e2d5]"
                    />
                  </div>
                )}

                {/* Aktions-Link */}
                <div className="pt-2 border-t border-[#e8e2d5]">
                  {isLocked ? (
                    <Link href="/app">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#8b6914] hover:text-[#a67c1a] transition-colors cursor-pointer">
                        <Star className="w-3.5 h-3.5" />
                        {t("programme.upgradeLink")}
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </Link>
                  ) : isPurchasable ? (
                    <div className="space-y-1.5">
                      <button
                        onClick={() => handleBuyKurs(program.id)}
                        disabled={kursCheckoutMutation.isPending}
                        className="inline-flex items-center gap-1 text-sm font-medium text-[#6b7c45] hover:text-[#5a6b38] transition-colors cursor-pointer disabled:opacity-50"
                      >
                        {kursCheckoutMutation.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        {t("programme.jetztKaufen", { price: String(program.price ?? "") })}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <p className="text-[11px] text-[#8b6914] flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        {t("programme.kostenlosBeiUpgrade")}
                      </p>
                    </div>
                  ) : (
                    <Link href={`/programme/${program.id}`}>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#6b7c45] hover:text-[#5a6b38] transition-colors cursor-pointer">
                        {isEnrolled ? t("programme.weiterMachen") : t("programme.detailsAnsehen")}
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(!programs || programs.length === 0) && (
        <div className="text-center py-16">
          <GraduationCap className="w-16 h-16 mx-auto text-[#d4c9a8] mb-4" />
          <h3 className="text-lg font-medium text-[#5a5a4a]">{t("programme.keineProgramme")}</h3>
          <p className="text-sm text-[#8a7d65] mt-1">{t("programme.keineProgrammeDesc")}</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  Programm-Detail (Module + Aufgaben)
// ═══════════════════════════════════════════════════════════
function ProgramDetail({ clientId, programId, isPremium }: { clientId: string; programId: string; isPremium: boolean }) {
  const { t } = useI18n();
  const { data: program, isLoading } = trpc.programs.getDetails.useQuery({ clientId, programId });
  const completeTaskMutation = trpc.programs.completeTask.useMutation();
  const updateStatusMutation = trpc.programs.updateStatus.useMutation();
  const enrollMutation = trpc.programs.enroll.useMutation();
  const utils = trpc.useUtils();
  const [, setLocation] = useLocation();
  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  const handleCompleteTask = async (moduleId: string, taskId: string) => {
    try {
      const result = await completeTaskMutation.mutateAsync({
        clientId,
        programId,
        moduleId,
        taskId,
      });
      utils.programs.getDetails.invalidate();
      utils.programs.list.invalidate();

      if (result.programCompleted) {
        toast.success(t("programme.programmAbgeschlossen"), {
          description: t("programme.glueckwunsch"),
          duration: 5000,
        });
      } else {
        toast.success(t("programme.aufgabeErledigt"), {
          description: t("programme.fortschrittLabel", { percent: String(result.progress.percent) }),
        });
      }
    } catch (err: any) {
      toast.error(err.message || t("programme.speichernFehler"));
    }
  };

  const handleEnroll = async () => {
    try {
      await enrollMutation.mutateAsync({ clientId, programId });
      utils.programs.getDetails.invalidate();
      utils.programs.list.invalidate();
      toast.success(t("programme.eingeschriebenJetzt"));
    } catch (err: any) {
      toast.error(err.message || t("programme.einschreibungFehler"));
    }
  };

  const handleStatusChange = async (status: "active" | "paused" | "abandoned") => {
    try {
      await updateStatusMutation.mutateAsync({ clientId, programId, status });
      utils.programs.getDetails.invalidate();
      utils.programs.list.invalidate();
      const messages: Record<string, string> = {
        active: t("programme.programmFortgesetzt"),
        paused: t("programme.programmPausiert"),
        abandoned: t("programme.programmAbgebrochen"),
      };
      toast.info(messages[status]);
    } catch (err: any) {
      toast.error(err.message || t("programme.aktualisierungFehler"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#6b7c45]" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-[#5a5a4a]">{t("programme.programmNichtGefunden")}</p>
        <Link href="/programme">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("common.back")}
          </Button>
        </Link>
      </div>
    );
  }

  const isEnrolled = !!program.enrollment;
  const isCompleted = program.enrollment?.status === "completed";
  const isPaused = program.enrollment?.status === "paused";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Zurück zum Katalog */}
      <Link href="/programme">
        <button className="flex items-center gap-2 text-sm text-[#6b7c45] hover:text-[#5a6b38] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          {t("programme.zurueckKatalog")}
        </button>
      </Link>

      {/* Programm-Header */}
      <div
        className="rounded-2xl p-6 mb-8"
        style={{
          background: `linear-gradient(135deg, ${program.color}15 0%, ${program.color}08 100%)`,
          border: `1px solid ${program.color}30`,
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl shrink-0"
            style={{ backgroundColor: `${program.color}20` }}
          >
            {program.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs" style={{ borderColor: program.color, color: program.color }}>
                {t("programme.wochen", { n: String(program.durationWeeks) })}
              </Badge>
              {program.accessLevel === "premium" && (
                <Badge className="text-xs bg-[#c9a84c] text-white">
                  <Star className="w-3 h-3 mr-1" /> {t("programme.dna")}
                </Badge>
              )}
              {isCompleted && (
                <Badge className="text-xs bg-[#6b7c45] text-white">
                  <Trophy className="w-3 h-3 mr-1" /> {t("programme.abgeschlossenBadge")}
                </Badge>
              )}
            </div>
            <h1
              className="text-2xl font-bold text-[#2c3e1f]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {program.title}
            </h1>
            <p className="text-sm text-[#6b7c45] italic">{program.subtitle}</p>
            <p className="text-sm text-[#5a5a4a] mt-2 leading-relaxed">{program.description}</p>
          </div>
        </div>

        {/* Fortschrittsbalken */}
        {isEnrolled && program.progress && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-[#5a5a4a]">
                {t("programme.aufgabenVon", { completed: String(program.progress.completedTasks), total: String(program.progress.totalTasks) })}
              </span>
              <span className="font-medium" style={{ color: program.color }}>
                {program.progress.percent}%
              </span>
            </div>
            <Progress value={program.progress.percent} className="h-3 bg-[#e8e2d5]" />
          </div>
        )}

        {/* Aktions-Buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          {!isEnrolled && (
            <Button
              onClick={handleEnroll}
              disabled={enrollMutation.isPending}
              className="bg-[#6b7c45] hover:bg-[#5a6b38] text-white"
            >
              {enrollMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {t("programme.jetzEinschreiben")}
            </Button>
          )}
          {isEnrolled && !isCompleted && !isPaused && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStatusChange("paused")}
              className="border-orange-300 text-orange-600 hover:bg-orange-50"
            >
              <Pause className="w-4 h-4 mr-1" /> {t("programme.pausieren")}
            </Button>
          )}
          {isPaused && (
            <Button
              size="sm"
              onClick={() => handleStatusChange("active")}
              className="bg-[#6b7c45] hover:bg-[#5a6b38] text-white"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> {t("programme.fortsetzenBtn")}
            </Button>
          )}
        </div>
      </div>

      {/* Module */}
      <div className="space-y-4">
        <h2
          className="text-lg font-bold text-[#2c3e1f]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {t("programme.wochenplan")}
        </h2>

        {program.modules.map((module) => {
          const isExpanded = expandedModule === module.id;
          const moduleProgress = module.totalTasks > 0
            ? Math.round((module.completedTasks / module.totalTasks) * 100)
            : 0;
          const isModuleComplete = moduleProgress === 100;
          const isCurrentWeek = program.enrollment?.currentWeek === module.weekNumber;

          return (
            <Card
              key={module.id}
              className={`overflow-hidden transition-all duration-300 ${
                isCurrentWeek ? "ring-2 ring-[#6b7c45]/30" : ""
              } ${isModuleComplete ? "bg-[#f0f5e8]" : "bg-[#faf8f3]"}`}
              style={{ borderColor: isCurrentWeek ? program.color : "#d4c9a8" }}
            >
              {/* Modul-Header (klickbar) */}
              <button
                onClick={() => setExpandedModule(isExpanded ? null : module.id)}
                className="w-full p-4 flex items-center gap-4 text-left hover:bg-[#f5f0e6]/50 transition-colors"
              >
                {/* Wochen-Nummer */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
                    isModuleComplete
                      ? "bg-[#6b7c45] text-white"
                      : isCurrentWeek
                      ? "bg-[#6b7c45]/10 text-[#6b7c45] ring-2 ring-[#6b7c45]"
                      : "bg-[#e8e2d5] text-[#8a7d65]"
                  }`}
                >
                  {isModuleComplete ? <CheckCircle2 className="w-5 h-5" /> : module.weekNumber}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#2c3e1f] truncate">{module.title}</h3>
                    {isCurrentWeek && !isModuleComplete && (
                      <Badge className="text-xs bg-[#6b7c45] text-white shrink-0">{t("programme.aktuelleWoche")}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-[#6b7c45] italic">{module.titleSanskrit}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-[#8a7d65]">
                      {t("programme.aufgabenVon", { completed: String(module.completedTasks), total: String(module.totalTasks) })}
                    </span>
                    {moduleProgress > 0 && (
                      <Progress value={moduleProgress} className="h-1.5 w-20 bg-[#e8e2d5]" />
                    )}
                  </div>
                </div>

                <ChevronRight
                  className={`w-5 h-5 text-[#8a7d65] transition-transform ${isExpanded ? "rotate-90" : ""}`}
                />
              </button>

              {/* Modul-Details (aufgeklappt) */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-[#d4c9a8]/30">
                  {/* Beschreibung */}
                  <p className="text-sm text-[#5a5a4a] mt-3 mb-4 leading-relaxed">{module.description}</p>

                  {/* Lernziele */}
                  {module.learningGoals && module.learningGoals.length > 0 && (
                    <div className="mb-4 p-3 rounded-lg bg-[#6b7c45]/5">
                      <h4 className="text-xs font-semibold text-[#6b7c45] uppercase tracking-wide mb-2">
                        {t("programme.lernziele")}
                      </h4>
                      <ul className="space-y-1">
                        {module.learningGoals.map((goal: string, i: number) => (
                          <li key={i} className="text-sm text-[#5a5a4a] flex items-start gap-2">
                            <Star className="w-3 h-3 text-[#c9a84c] mt-1 shrink-0" />
                            {goal}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Audio-Session + Rezept-Hinweis (klickbar) */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {module.audioSessionTitle && (
                      <button
                        onClick={() => setLocation("/sessions")}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-[#6b7c45]/30 text-[#6b7c45] bg-[#6b7c45]/5 hover:bg-[#6b7c45]/15 hover:border-[#6b7c45]/50 transition-all cursor-pointer"
                      >
                        <Headphones className="w-3 h-3" />
                        {t("programme.audio")} {module.audioSessionTitle}
                      </button>
                    )}
                    {module.recipeTheme && (
                      <button
                        onClick={() => setLocation("/rezepte")}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-[#c9a84c]/30 text-[#c9a84c] bg-[#c9a84c]/5 hover:bg-[#c9a84c]/15 hover:border-[#c9a84c]/50 transition-all cursor-pointer"
                      >
                        <Utensils className="w-3 h-3" />
                        {t("programme.rezept")} {module.recipeTheme}
                      </button>
                    )}
                  </div>

                  {/* Aufgaben-Liste */}
                  <div className="space-y-2">
                    {module.tasks.map((task) => {
                      const taskTypeIcons: Record<string, React.ReactNode> = {
                        reading: <BookOpen className="w-4 h-4" />,
                        practice: <Play className="w-4 h-4" />,
                        reflection: <Sparkles className="w-4 h-4" />,
                        exercise: <Star className="w-4 h-4" />,
                        meditation: <Sparkles className="w-4 h-4" />,
                        journaling: <BookOpen className="w-4 h-4" />,
                        cooking: <Star className="w-4 h-4" />,
                        quiz: <GraduationCap className="w-4 h-4" />,
                      };

                      return (
                        <div
                          key={task.id}
                          className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                            task.completed
                              ? "bg-[#6b7c45]/10"
                              : "bg-white hover:bg-[#f5f0e6]"
                          }`}
                        >
                          {/* Checkbox */}
                          <button
                            onClick={() => {
                              if (!task.completed && isEnrolled) {
                                handleCompleteTask(module.id, task.id);
                              }
                            }}
                            disabled={task.completed || !isEnrolled || completeTaskMutation.isPending}
                            className={`mt-0.5 shrink-0 ${
                              task.completed
                                ? "text-[#6b7c45]"
                                : "text-[#d4c9a8] hover:text-[#6b7c45]"
                            }`}
                          >
                            {task.completed ? (
                              <CheckCircle2 className="w-5 h-5" />
                            ) : (
                              <Circle className="w-5 h-5" />
                            )}
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-medium ${task.completed ? "text-[#6b7c45] line-through" : "text-[#2c3e1f]"}`}>
                                {task.title}
                              </span>
                              <span className="text-[#8a7d65]">
                                {taskTypeIcons[task.type] || <Circle className="w-4 h-4" />}
                              </span>
                            </div>
                            <p className="text-xs text-[#8a7d65] mt-0.5">{task.description}</p>
                            {task.durationMinutes && (
                              <span className="text-xs text-[#b0a58c] flex items-center gap-1 mt-1">
                                <Clock className="w-3 h-3" />
                                {task.durationMinutes} {t("programme.minLabel")}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Bonus-Info */}
      {program.completionPoints && (
        <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-[#c9a84c]/10 to-[#6b7c45]/10 border border-[#c9a84c]/20 text-center">
          <Trophy className="w-8 h-8 mx-auto text-[#c9a84c] mb-2" />
          <p className="text-sm font-medium text-[#2c3e1f]">
            {t("programme.bonus", { points: String(program.completionPoints) })}
          </p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  Haupt-Komponente: Routing zwischen Liste und Detail
// ═══════════════════════════════════════════════════════════
export default function AppProgramme() {
  const { client, loading } = useClientAuth();
  const [, params] = useRoute("/programme/:programId");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#6b7c45]" />
      </div>
    );
  }

  if (!client) return null;

  const isPremium = client.packageType === "premium";

  // Wenn eine programId in der URL ist, zeige Detail-Ansicht
  if (params?.programId) {
    return <ProgramDetail clientId={client.clientId} programId={params.programId} isPremium={isPremium} />;
  }

  // Sonst zeige die Übersicht
  return <ProgramList clientId={client.clientId} isPremium={isPremium} />;
}
